import React from 'react';
import { Modal } from 'react-bootstrap';
import { AiOutlineClose } from 'react-icons/ai';
import { FaRegHandshake } from 'react-icons/fa';
import { FiCalendar, FiClock } from 'react-icons/fi';
import {
  diaDaSemana,
  diaSeguinte,
  formatarData,
  formatarDataHora,
} from '~/config/encerramentoSistema';
import CartaoContato from './CartaoContato';
import ContadorRegressivo from './ContadorRegressivo';
import {
  BotaoFecharModal,
  BotaoPrimario,
  CabecalhoModal,
  CorpoModal,
  DestaquePrazo,
  EstiloGlobalModal,
  IconeCabecalho,
  RodapeModal,
} from './styles';

const UMA_HORA_EM_MS = 60 * 60 * 1000;

export type PrazoComunicado =
  | { tipo: 'aviso'; ultimoDiaDeAcesso: string; diasAteUltimoDia: number }
  | { tipo: 'liberacao'; inicioLiberacao: number; fimLiberacao: number };

interface ModalComunicadoProps {
  show: boolean;
  onClose(): void;
  prazo: PrazoComunicado;
}

const ConteudoAviso: React.FC<{
  ultimoDiaDeAcesso: string;
  diasAteUltimoDia: number;
}> = ({ ultimoDiaDeAcesso, diasAteUltimoDia }) => {
  const ultimoDia = `${diaDaSemana(ultimoDiaDeAcesso)}, ${formatarData(
    ultimoDiaDeAcesso,
  )}`;

  let tituloPrazo = `Faltam ${diasAteUltimoDia} dias`;
  let detalhePrazo = `para o último dia de acesso (${formatarData(
    ultimoDiaDeAcesso,
  )})`;

  if (diasAteUltimoDia <= 1) {
    tituloPrazo =
      diasAteUltimoDia === 0
        ? 'Hoje é o último dia de acesso'
        : 'Amanhã é o último dia de acesso';
    detalhePrazo = ultimoDia;
  }

  return (
    <>
      <p>
        Agradecemos sinceramente pela confiança e pela parceria ao longo de toda
        essa jornada.
      </p>
      <p>
        Informamos que o acesso a este sistema será encerrado. O último dia de
        utilização será <strong>{ultimoDia}</strong> e, a partir de{' '}
        <strong>{formatarData(diaSeguinte(ultimoDiaDeAcesso))}</strong>, o
        acesso não estará mais disponível.
      </p>

      <DestaquePrazo urgente={diasAteUltimoDia <= 2}>
        <FiCalendar size={24} aria-hidden />
        <div>
          <strong>{tituloPrazo}</strong>
          <span>{detalhePrazo}</span>
        </div>
      </DestaquePrazo>
    </>
  );
};

const ConteudoLiberacao: React.FC<{
  inicioLiberacao: number;
  fimLiberacao: number;
}> = ({ inicioLiberacao, fimLiberacao }) => {
  const horas = Math.round((fimLiberacao - inicioLiberacao) / UMA_HORA_EM_MS);

  return (
    <>
      <p>
        O acesso a este sistema foi <strong>liberado temporariamente</strong>{' '}
        por {horas} horas, para que você possa concluir suas pendências.
      </p>
      <p>
        Recomendamos salvar ou exportar agora as informações de que precisar. Em{' '}
        <strong>{formatarDataHora(fimLiberacao, true)}</strong>, o acesso será
        encerrado novamente.
      </p>

      <DestaquePrazo urgente>
        <FiClock size={24} aria-hidden />
        <div>
          <strong>
            Tempo restante: <ContadorRegressivo fim={fimLiberacao} />
          </strong>
          <span>até {formatarDataHora(fimLiberacao)}</span>
        </div>
      </DestaquePrazo>
    </>
  );
};

const ModalComunicado: React.FC<ModalComunicadoProps> = ({
  show,
  onClose,
  prazo,
}) => (
  <>
    <EstiloGlobalModal />
    <Modal
      show={show}
      onHide={onClose}
      centered
      className="modal-comunicado-encerramento"
      backdropClassName="modal-comunicado-encerramento-fundo"
      dialogClassName="modal-comunicado-encerramento-dialogo"
      contentClassName="modal-comunicado-encerramento-conteudo"
      aria-labelledby="titulo-comunicado-encerramento"
    >
      <CabecalhoModal>
        <BotaoFecharModal
          type="button"
          onClick={onClose}
          aria-label="Fechar comunicado"
        >
          <AiOutlineClose size={18} />
        </BotaoFecharModal>
        <IconeCabecalho>
          <FaRegHandshake size={30} aria-hidden />
        </IconeCabecalho>
        <h2 id="titulo-comunicado-encerramento">Comunicado importante</h2>
        <span>
          {prazo.tipo === 'liberacao'
            ? 'Liberação temporária de acesso'
            : 'Uma mensagem especial para você, nosso cliente'}
        </span>
      </CabecalhoModal>

      <CorpoModal>
        <p>Prezado(a) cliente,</p>

        {prazo.tipo === 'liberacao' ? (
          <ConteudoLiberacao
            inicioLiberacao={prazo.inicioLiberacao}
            fimLiberacao={prazo.fimLiberacao}
          />
        ) : (
          <ConteudoAviso
            ultimoDiaDeAcesso={prazo.ultimoDiaDeAcesso}
            diasAteUltimoDia={prazo.diasAteUltimoDia}
          />
        )}

        <CartaoContato />
      </CorpoModal>

      <RodapeModal>
        <BotaoPrimario type="button" onClick={onClose}>
          Entendi
        </BotaoPrimario>
      </RodapeModal>
    </Modal>
  </>
);

export default ModalComunicado;
